import rawSchema from '@/data/ers_schema_v1.0.json';
import { zErsSchema } from './zod';
import type {
  ErsSchema,
  ExplanationOfTerms,
  Indicator,
  Instrument,
  Item,
  RatingScaleLevel,
  ScoringLevel,
  Subscale,
} from './types';

/**
 * Load + validate the ERS schema at module-evaluation time.
 * If the JSON drifts from ./types.ts, this throws with a readable Zod message.
 * The result is deep-frozen so consumers can safely treat it as read-only.
 */

function deepFreeze<T>(value: T): T {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const key of Object.keys(value as object)) {
      deepFreeze((value as Record<string, unknown>)[key]);
    }
  }
  return value;
}

const parsed = zErsSchema.safeParse(rawSchema);
if (!parsed.success) {
  // Surface the exact drift in dev — this should never happen in prod.
  throw new Error(
    `ERS schema validation failed:\n${parsed.error.issues
      .map((i) => `  · ${i.path.join('.') || '(root)'}: ${i.message}`)
      .join('\n')}`,
  );
}

export const schema: ErsSchema = deepFreeze(parsed.data as ErsSchema);

/* ------------------------------------------------------------------ */
/* Lookups                                                             */
/* ------------------------------------------------------------------ */

const instrumentById = new Map<string, Instrument>();
const subscaleById = new Map<string, Subscale>();
const itemById = new Map<string, Item>();
const indicatorById = new Map<string, Indicator>();
const subscaleOfItem = new Map<string, Subscale>();
const instrumentOfSubscale = new Map<string, Instrument>();

for (const instrument of schema.instruments) {
  instrumentById.set(instrument.id, instrument);
  for (const subscale of instrument.subscales) {
    subscaleById.set(subscale.id, subscale);
    instrumentOfSubscale.set(subscale.id, instrument);
    for (const item of subscale.items) {
      itemById.set(item.id, item);
      subscaleOfItem.set(item.id, subscale);
      for (const indicator of item.indicators) {
        indicatorById.set(indicator.id, indicator);
      }
    }
  }
}

export const instruments = schema.instruments;

export function getInstrument(id: string): Instrument | undefined {
  return instrumentById.get(id);
}

export function getSubscale(id: string): Subscale | undefined {
  return subscaleById.get(id);
}

export function getItem(id: string): Item | undefined {
  return itemById.get(id);
}

export function getIndicator(id: string): Indicator | undefined {
  return indicatorById.get(id);
}

export function getSubscaleOfItem(itemId: string): Subscale | undefined {
  return subscaleOfItem.get(itemId);
}

export function getInstrumentOfSubscale(
  subscaleId: string,
): Instrument | undefined {
  return instrumentOfSubscale.get(subscaleId);
}

/**
 * Ordered list of items across an instrument (S1→S6, I1→In).
 * Useful for next/previous navigation regardless of which subscale we're in.
 */
export function itemsInOrder(instrumentId: string): Item[] {
  const instrument = getInstrument(instrumentId);
  if (!instrument) return [];
  return instrument.subscales.flatMap((s) => s.items);
}

export function ratingLevel(score: number): RatingScaleLevel | undefined {
  return schema.rating_scale_levels.find((l) => l.score === score);
}

export const SCORED_LEVELS: readonly ScoringLevel[] = [1, 3, 5, 7] as const;

/** Group an item's indicators by their scoring level (1/3/5/7). */
export function indicatorsByLevel(
  item: Item,
): Record<ScoringLevel, Indicator[]> {
  const out: Record<ScoringLevel, Indicator[]> = { 1: [], 3: [], 5: [], 7: [] };
  for (const indicator of item.indicators) {
    out[indicator.level].push(indicator);
  }
  for (const level of SCORED_LEVELS) {
    out[level].sort((a, b) => a.sequence - b.sequence);
  }
  return out;
}

/**
 * The manual's "Explanation of Terms Used Throughout the Scale" (pp. 12-14).
 * 19 Notes for Clarification say "see the Explanation of Terms", so the
 * glossary needs to be reachable from wherever those notes are read.
 */
export function explanationOfTerms(
  instrumentId: string = DEFAULT_INSTRUMENT_ID,
): ExplanationOfTerms | undefined {
  return getInstrument(instrumentId)?.explanation_of_terms;
}

/** Default instrument for the MVP. */
export const DEFAULT_INSTRUMENT_ID = 'FCCERS-3';
